// What the API reference shows: endpoints, fields, errors, limits and example responses. The examples are real
// responses from the API (a Harvard lecture processed on QA, trimmed where noted). public/openapi.json describes the
// same API, and apiDocsContent.test.js keeps the two in step.

export const PUBLIC_BASE_URL = 'https://bumpups.com/api/v1';
export const SAMPLE_VIDEO_URL = 'https://www.youtube.com/watch?v=kBdfcR-8hEY';
const VIDEO_ID = 'sRV9zsGtyJRc2apg';
const QUESTION_ID = 'kEV7Adv4Ed0RyJfI';
const TITLE = 'Justice: What\'s The Right Thing To Do? Episode 01 "THE MORAL SIDE OF MURDER"';
const QUESTION = 'Which cases does the professor use to test our moral intuitions?';

// The lecture's first chapters (15 in total)
export const SAMPLE_CHAPTERS = [
  { start_seconds: 0, timestamp: '0:00', title: 'Intro credits and course framing' },
  { start_seconds: 150, timestamp: '2:30', title: 'Audience poll: trolley dilemma choices' },
  { start_seconds: 327, timestamp: '5:27', title: 'Bridge variant: would you push the fat man?' },
  { start_seconds: 601, timestamp: '10:01', title: 'Transplant case: save five or save one?' },
  { start_seconds: 723, timestamp: '12:03', title: 'Debate: yank organs from healthy patient?' },
  { start_seconds: 912, timestamp: '15:12', title: 'Categorical moral reasoning introduced' },
  { start_seconds: 1775, timestamp: '29:35', title: 'Queen v. Dudley and Stephens case introduced' },
];

const videoBase = {
  object: 'video',
  id: VIDEO_ID,
  youtube_id: 'kBdfcR-8hEY',
  title: TITLE,
  duration_seconds: 3296,
  minutes_charged: 54,
};

const PROCESSING_VIDEO = {
  ...videoBase,
  status: 'processing',
  chapters: null,
  chapters_text: null,
  error: null,
  app_url: `https://bumpups.com/workspace/${VIDEO_ID}`,
  created_at: '2026-10-09T20:51:39Z',
};

const READY_VIDEO = {
  ...videoBase,
  status: 'ready',
  chapters: SAMPLE_CHAPTERS.slice(0, 3),
  chapters_text: SAMPLE_CHAPTERS.slice(0, 3).map(c => `${c.timestamp} - ${c.title}`).join('\n'),
  error: null,
  app_url: `https://bumpups.com/workspace/${VIDEO_ID}`,
  created_at: '2026-10-09T20:51:39Z',
};

const questionBase = { object: 'question', id: QUESTION_ID, video_id: VIDEO_ID, question: QUESTION, created_at: '2026-10-09T21:37:33Z' };

export const SAMPLE_ANSWER = [
  'The professor presents five main cases to test moral intuitions:',
  '',
  '*   **The trolley car driver:** You are driving a runaway trolley and must choose between staying on the track to kill five workers or turning onto a side track to kill one.',
  '*   **The onlooker on the bridge:** You are watching a runaway trolley and can save five workers on the track by pushing a heavy man off a bridge to stop the trolley, killing him.',
  '*   **The transplant surgeon:** You have five patients who will die without organ transplants. You can save them by killing a healthy patient who came in for a checkup and harvesting his organs.',
].join('\n');

const PENDING_QUESTION = { ...questionBase, status: 'pending', answer: null, error: null };
const ANSWERED_QUESTION = { ...questionBase, status: 'answered', answer: SAMPLE_ANSWER, error: null };

const ACCOUNT = {
  object: 'account', plan: 'pro', minutes_limit: 1000, minutes_used: 67, minutes_left: 933, resets_at: '2026-11-01T00:00:00Z',
};

export const ERROR_EXAMPLE = {
  type: 'https://bumpups.com/api/docs#not_enough_minutes',
  title: 'Not enough minutes',
  status: 402,
  detail: 'This video needs 54 minutes and you have 17 left this cycle.',
  code: 'not_enough_minutes',
  request_id: 'req_8fK2mQ7xLp3aB9cD0eF1',
  minutes_needed: 54,
  minutes_left: 17,
};

export const ENDPOINTS = [
  {
    id: 'create-video',
    group: 'Videos',
    method: 'POST',
    path: '/videos',
    title: 'Process a video',
    summary: 'Sends a YouTube link to Bump AI. The video costs its length in whole minutes, charged once. It shows up in your Workspace too, where you can open it, chat about it and export it.',
    headers: [['Idempotency-Key', 'string', false, 'Any unique string up to 255 characters. Retrying with the same key within 24 hours returns the same video and never charges again. A retry sent while the first request is still running gets 409.']],
    body: [['url', 'string', true, 'A YouTube video link: watch, youtu.be, shorts, live or embed.']],
    requestBody: { url: SAMPLE_VIDEO_URL },
    response: { status: 202, label: 'Accepted', headers: `Location: /api/v1/videos/${VIDEO_ID}\nRetry-After: 60`, body: PROCESSING_VIDEO },
    notes: 'Videos under 30 minutes are usually ready in 1–2 minutes, longer ones in 5–15. Check back at the Location URL after the Retry-After seconds.',
  },
  {
    id: 'get-video',
    group: 'Videos',
    method: 'GET',
    path: '/videos/{id}',
    pathExample: `/videos/${VIDEO_ID}`,
    title: 'Get a video',
    summary: 'The video\'s status and, once it\'s ready, its chapters, both as a list and as text ready to paste into a YouTube description.',
    params: [['id', 'string', true, 'The id from Process a video. YouTube videos you added in the web app work too.']],
    response: { status: 200, label: 'OK', body: READY_VIDEO, trimmed: 'Trimmed to the first 3 of 15 chapters.' },
  },
  {
    id: 'ask-question',
    group: 'Questions',
    method: 'POST',
    path: '/videos/{id}/questions',
    pathExample: `/videos/${VIDEO_ID}/questions`,
    title: 'Ask a question',
    summary: 'Asks Bump AI about a ready video. Questions don\'t use minutes. Each video allows 25, counted together with web chat, and one is answered at a time. Answers appear in a chat named “API” on the video, and later questions keep the context.',
    params: [['id', 'string', true, 'The video\'s id.']],
    body: [['question', 'string', true, 'Up to 2,000 characters.']],
    requestBody: { question: QUESTION },
    response: { status: 202, label: 'Accepted', headers: `Location: /api/v1/videos/${VIDEO_ID}/questions/${QUESTION_ID}\nRetry-After: 10`, body: PENDING_QUESTION },
  },
  {
    id: 'get-question',
    group: 'Questions',
    method: 'GET',
    path: '/videos/{id}/questions/{question_id}',
    pathExample: `/videos/${VIDEO_ID}/questions/${QUESTION_ID}`,
    title: 'Get an answer',
    summary: 'The question\'s status and, once answered, the answer in Markdown, the same answer Bump AI gives in chat.',
    params: [['id', 'string', true, 'The video\'s id.'], ['question_id', 'string', true, 'The id from Ask a question.']],
    response: { status: 200, label: 'OK', body: ANSWERED_QUESTION, trimmed: 'Trimmed to the first 3 of 5 cases.' },
  },
  {
    id: 'get-account',
    group: 'Account',
    method: 'GET',
    path: '/account',
    title: 'Get your minutes',
    summary: 'Your plan, the minutes you have left (the same balance as the web app) and when they reset.',
    response: { status: 200, label: 'OK', body: ACCOUNT },
  },
];

// [field, type, description]
export const OBJECTS = [
  {
    id: 'video-object',
    title: 'The video object',
    fields: [
      ['id', 'string', 'The video\'s id, also used in its Workspace link.'],
      ['status', 'string', 'processing, ready or failed.'],
      ['youtube_id', 'string', 'The YouTube video id.'],
      ['title', 'string', 'The YouTube title.'],
      ['duration_seconds', 'integer', 'The video\'s length.'],
      ['minutes_charged', 'integer', 'Minutes used. 0 when processing failed, because the minutes were refunded.'],
      ['chapters', 'array | null', 'Once ready: { start_seconds, timestamp, title } for each chapter.'],
      ['chapters_text', 'string | null', 'Once ready: the chapters as lines like 0:00 - Intro, for a YouTube description.'],
      ['error', 'object | null', 'When failed: { code, message }.'],
      ['app_url', 'string', 'The video in the Bumpups web app.'],
      ['created_at', 'string', 'When it was added (RFC 3339, UTC).'],
    ],
  },
  {
    id: 'question-object',
    title: 'The question object',
    fields: [
      ['id', 'string', 'The question\'s id.'],
      ['video_id', 'string', 'The video it\'s about.'],
      ['status', 'string', 'pending, answered or failed.'],
      ['question', 'string', 'What you asked.'],
      ['answer', 'string | null', 'Once answered: Markdown, citing moments as m:ss.'],
      ['error', 'object | null', 'When failed: { code, message }. Failed questions aren\'t counted.'],
      ['created_at', 'string', 'When it was asked (RFC 3339, UTC).'],
    ],
  },
];

// [HTTP status, codes, meaning]
export const ERRORS = [
  [400, ['invalid_request', 'invalid_url'], 'A field is missing, unknown or the wrong type, or the link isn\'t a YouTube video.'],
  [401, ['invalid_api_key'], 'The key is missing, wrong or revoked.'],
  [402, ['not_enough_minutes'], 'The video needs more minutes than you have left. See minutes_needed and minutes_left.'],
  [403, ['pro_required'], 'The account isn\'t on Pro. Keys start working again after an upgrade.'],
  [404, ['not_found'], 'No such endpoint, or no such video or question in your account.'],
  [405, ['method_not_allowed'], 'Use the method in the Allow header.'],
  [409, ['video_not_ready', 'question_in_progress', 'idempotency_key_in_use'], 'Wait until the video is ready, until the last answer arrives, or until the first request with this Idempotency-Key finishes. Retry-After says how long.'],
  [413, ['payload_too_large'], 'Request bodies can be up to 16 KB.'],
  [415, ['unsupported_media_type'], 'Send JSON with Content-Type: application/json.'],
  [422, ['video_not_supported', 'idempotency_key_reused'], 'The video is private, live, over 3 hours, or unlisted and under 30 minutes (see reason), or the Idempotency-Key was already used for another video.'],
  [429, ['question_limit_reached'], 'The video has used its 25 questions (web chat and API together), or it already has 7 chats.'],
  [500, ['internal_error'], 'Something failed on our side. Retry, and quote the request_id if it keeps happening.'],
  [503, ['unavailable'], 'Maintenance, or YouTube didn\'t answer. Retry after the Retry-After seconds.'],
];

export const LIMITS = [
  ['Plan', 'Pro. Keys stop working while an account is on Free and start again after an upgrade.'],
  ['Minutes', 'A video costs its length in whole minutes (at least 1), from the same balance as the web app. No chapters, no charge.'],
  ['Questions', '25 per video, counted together with web chat, one at a time. They don\'t use minutes.'],
  ['Videos', 'Public YouTube videos up to 3 hours. Unlisted videos need to be 30 minutes or longer. Live streams and premieres aren\'t supported.'],
  ['Keys', 'Up to 5 per account. Revoked keys stop working within a minute.'],
  ['Requests', 'JSON bodies up to 16 KB. Keys are for servers: browsers can\'t call the API.'],
];

const pathOf = endpoint => endpoint.pathExample || endpoint.path;

// A ready-to-run request for an endpoint, in curl, Python or JavaScript (Node 18+)
export function requestSnippet(lang, endpoint, baseUrl) {
  const url = baseUrl + pathOf(endpoint);
  const body = endpoint.requestBody;
  const idempotent = (endpoint.headers || []).some(([name]) => name === 'Idempotency-Key');
  if (lang === 'curl') {
    const lines = [`curl${endpoint.method === 'GET' ? '' : ` -X ${endpoint.method}`} ${url}`, '  -H "Authorization: Bearer $BUMPUPS_API_KEY"'];
    if (body) lines.push('  -H "Content-Type: application/json"');
    if (idempotent) lines.push('  -H "Idempotency-Key: $(uuidgen)"');
    if (body) lines.push(`  -d '${JSON.stringify(body)}'`);
    return lines.join(' \\\n');
  }
  if (lang === 'python') {
    const args = [`"${url}"`, idempotent ? 'headers={**HEADERS, "Idempotency-Key": str(uuid.uuid4())}' : 'headers=HEADERS'];
    if (body) args.push(`json=${JSON.stringify(body)}`);
    return [
      `import os${idempotent ? ', uuid' : ''}, requests`,
      '',
      'HEADERS = {"Authorization": f"Bearer {os.environ[\'BUMPUPS_API_KEY\']}"}',
      '',
      `response = requests.${endpoint.method.toLowerCase()}(${args.join(', ')})`,
      'print(response.status_code, response.json())',
    ].join('\n');
  }
  const headers = ["Authorization: 'Bearer ' + process.env.BUMPUPS_API_KEY"];
  if (body) headers.push("'Content-Type': 'application/json'");
  if (idempotent) headers.push("'Idempotency-Key': crypto.randomUUID()");
  const options = [`method: '${endpoint.method}'`, `headers: { ${headers.join(', ')} }`];
  if (body) options.push(`body: JSON.stringify(${JSON.stringify(body)})`);
  return [
    `const response = await fetch('${url}', {`,
    ...options.map(o => `  ${o},`),
    '});',
    'console.log(response.status, await response.json());',
  ].join('\n');
}
